import MonitorIntelligence from "../components/MonitorIntelligence";
import RequestSettings from "../components/RequestSettings";
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, RefreshCw, Loader2, ExternalLink, ShieldCheck, Globe2, Network, Zap, Clock, Activity,
  Lock, ArrowRightLeft, CornerDownRight, Check, X as XIcon, Pause, Play, Search as SearchIcon, Tag,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell,
} from "recharts";
import { format, parseISO } from "date-fns";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { StatusBadge } from "../components/StatusBadge";
import SubdomainDialog from "../components/SubdomainDialog";
import { staggerContainer, fadeUpItem } from "../components/Motion";
import {
  CHART_COLORS, fmtPct, fmtMs, fmtDate, fmtDay, fmtDuration,
  siteVariant, siteDisplay, sslVariant, domainVariant, dnsVariant, perfVariant,
  securityVariant, redirectVariant, SECURITY_HEADER_LABELS,
} from "../lib/monitor";

const RANGES = [
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
];

function labelFmt(label, range) {
  try {
    if (range === "24h") return label.length >= 13 ? label.slice(11, 13) + ":00" : label;
    return format(parseISO(label.slice(0, 10)), "d MMM");
  } catch {
    return label;
  }
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-md">
      <p className="mb-1 font-mono text-slate-400">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="font-mono font-medium text-slate-800">
          {p.name}: {p.value}
          {p.name === "uptime" ? "%" : p.name === "response" ? " ms" : ""}
        </p>
      ))}
    </div>
  );
}

function MetricTile({ icon: Icon, label, value, sub, variant, testid }) {
  return (
    <Card className="om-premium-card h-full min-w-0 p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400">
        <Icon size={15} strokeWidth={2} />
        <span className="text-[11px] font-mono uppercase tracking-[0.1em]">{label}</span>
      </div>
      <p className="mt-3 break-words font-mono text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">{value}</p>
      {sub && <div className="mt-1.5">{typeof sub === "string" ? <span className="text-xs text-slate-500">{sub}</span> : sub}</div>}
    </Card>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 py-2.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="max-w-full break-all text-right font-mono font-medium text-slate-900">{value ?? "—"}</span>
    </div>
  );
}

export default function WebsiteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [range, setRange] = useState("24h");
  const [history, setHistory] = useState({ bars: [], points: [] });
  const [incidents, setIncidents] = useState([]);
  const [incidentUpdating, setIncidentUpdating] = useState(null);
  const [incidentActionError, setIncidentActionError] = useState("");
  const [dnsChanges, setDnsChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showSubs, setShowSubs] = useState(false);

  const loadSite = useCallback(async () => {
    try {
      const [s, inc, dns] = await Promise.all([
        api.get(`/websites/${id}`),
        api.get(`/websites/${id}/incidents`),
        api.get(`/websites/${id}/dns-changes`),
      ]);
      setSite(s.data);
      setIncidents(inc.data);
      setDnsChanges(dns.data);
    } catch {
      navigate("/websites");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const updateIncident = async (incidentId, action) => {
    setIncidentUpdating(incidentId);
    setIncidentActionError("");
    try {
      const { data } = await api.post(`/websites/${id}/incidents/${incidentId}/action`, { action });
      setIncidents(previous => previous.map(incident => incident.id === incidentId ? data : incident));
    } catch (err) {
      setIncidentActionError(err.response?.data?.detail || "Could not update the incident. Please retry.");
    } finally {
      setIncidentUpdating(null);
    }
  };

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/websites/${id}/history`, { params: { range } });
      setHistory(data);
    } catch {
      /* ignore */
    }
  }, [id, range]);

  useEffect(() => {
    loadSite();
  }, [loadSite]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    const t = setInterval(() => { loadSite(); loadHistory(); }, 30000);
    return () => clearInterval(t);
  }, [loadSite, loadHistory]);

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([loadSite(), loadHistory()]);
    setRefreshing(false);
  };

  const togglePause = async () => {
    try {
      const { data } = await api.post(`/websites/${id}/pause`);
      setSite((s) => ({ ...s, paused: data.paused }));
    } catch {
      /* ignore */
    }
  };

  if (loading || !site) {
    return (
      <div className="space-y-8" data-testid="detail-skeleton">
        <div className="space-y-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 2xl:grid-cols-6">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="border-slate-200 p-5"><Skeleton className="h-3 w-20" /><Skeleton className="mt-3 h-7 w-24" /></Card>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card className="border-slate-200 p-6"><Skeleton className="mb-4 h-4 w-24" /><Skeleton className="h-52 w-full" /></Card>
          <Card className="border-slate-200 p-6"><Skeleton className="mb-4 h-4 w-32" /><Skeleton className="h-52 w-full" /></Card>
        </div>
      </div>
    );
  }

  const ssl = site.ssl || {};
  const domain = site.domain || {};
  const dns = site.dns || {};
  const perf = site.performance || {};
  const http = site.http || {};

  return (
    <div className="min-w-0 space-y-6 sm:space-y-8" data-testid="website-detail-page">
      {/* Header */}
      <div>
        <button onClick={() => navigate("/websites")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="back-btn">
          <ArrowLeft size={15} /> Webapps
        </button>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <h1 className="max-w-full break-words font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{site.name}</h1>
            <StatusBadge variant={siteDisplay(site).variant} pulse={!site.paused && site.status !== "UNKNOWN"} testid="detail-status-badge">{siteDisplay(site).label}</StatusBadge>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <a href={site.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
              <ExternalLink size={15} /> Visit
            </a>
            <Button variant="outline" onClick={() => setShowSubs(true)} data-testid="discover-subdomains-btn">
              <SearchIcon size={15} className="mr-1.5" /> Subdomains
            </Button>
            <Button variant="outline" onClick={togglePause} data-testid="detail-pause-btn">
              {site.paused ? <><Play size={15} className="mr-1.5" /> Resume</> : <><Pause size={15} className="mr-1.5" /> Pause</>}
            </Button>
            <Button variant="outline" onClick={refresh} disabled={refreshing} data-testid="detail-refresh-btn">
              <RefreshCw size={15} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>
        <p className="mt-2 break-all font-mono text-xs leading-6 text-slate-500 sm:text-sm">{site.url}</p>
        {(site.monitor_type === "keyword" || (site.tags && site.tags.length > 0)) && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {site.monitor_type === "keyword" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-mono font-medium text-violet-700" data-testid="detail-keyword-badge">
                <SearchIcon size={11} /> keyword "{site.keyword}" · {site.keyword_match || "present"}
              </span>
            )}
            {(site.tags || []).map((t) => (
              <span key={t} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-mono text-slate-600">
                <Tag size={11} /> {t}
              </span>
            ))}
          </div>
        )}
        {site.last_error && site.status === "DOWN" && (
          <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 font-mono text-xs text-red-600" data-testid="detail-error">{site.last_error}</p>
        )}
      </div>

      {/* Metric tiles */}
      <motion.div variants={staggerContainer} initial="hidden" animate="show" className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <motion.div variants={fadeUpItem}><MetricTile icon={Clock} label="Response Time" value={fmtMs(site.response?.current)} sub={`avg ${fmtMs(site.response?.average)}`} testid="tile-response" /></motion.div>
        <motion.div variants={fadeUpItem}><MetricTile icon={Activity} label="Uptime 24h" value={fmtPct(site.uptime?.["24h"])} sub={`7d ${fmtPct(site.uptime?.["7d"])} · 30d ${fmtPct(site.uptime?.["30d"])}`} testid="tile-uptime" /></motion.div>
        <motion.div variants={fadeUpItem}><MetricTile icon={ShieldCheck} label="SSL" value={ssl.status || "—"} sub={ssl.days_remaining != null ? `${ssl.days_remaining} days left` : ssl.error} testid="tile-ssl" /></motion.div>
        <motion.div variants={fadeUpItem}><MetricTile icon={Globe2} label="Domain" value={domain.status || "—"} sub={domain.days_remaining != null ? `${domain.days_remaining} days left` : domain.error} testid="tile-domain" /></motion.div>
        <motion.div variants={fadeUpItem}><MetricTile icon={Network} label="DNS" value={dns.status || "—"} testid="tile-dns" /></motion.div>
        <motion.div variants={fadeUpItem}><MetricTile icon={Zap} label="Performance" value={perf.status || "—"} sub={perf.page_load_ms != null ? `${(perf.page_load_ms / 1000).toFixed(1)} sec` : perf.error} testid="tile-perf" /></motion.div>
        <motion.div variants={fadeUpItem}><MetricTile icon={Lock} label="Security" value={http.security_grade ? `Grade ${http.security_grade}` : "—"} sub={http.security_score != null ? `${http.security_score}/6 headers` : http.error} testid="tile-security" /></motion.div>
      </motion.div>

      {/* Range selector */}
      <div className="flex items-center gap-2">
        {RANGES.map((r) => (
          <button
            key={r.value}
            onClick={() => setRange(r.value)}
            data-testid={`range-${r.value}`}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              range === r.value ? "bg-blue-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border-slate-200 p-6" data-testid="uptime-chart">
          <h3 className="mb-4 font-heading text-base font-semibold text-slate-800">Uptime</h3>
          {history.bars.length === 0 ? (
            <p className="py-16 text-center text-sm text-slate-400">Collecting data…</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={history.bars} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tickFormatter={(l) => labelFmt(l, range)} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} minTickGap={20} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "#F1F5F9" }} />
                <Bar dataKey="uptime" radius={[3, 3, 0, 0]}>
                  {history.bars.map((b, i) => (
                    <Cell key={i} fill={b.uptime >= 99 ? CHART_COLORS.up : b.uptime >= 90 ? CHART_COLORS.warn : CHART_COLORS.down} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="border-slate-200 p-6" data-testid="response-chart">
          <h3 className="mb-4 font-heading text-base font-semibold text-slate-800">Response Time</h3>
          {history.points.length === 0 ? (
            <p className="py-16 text-center text-sm text-slate-400">Collecting data…</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={history.points} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="time" tickFormatter={(t) => { try { return format(parseISO(t), range === "24h" ? "HH:mm" : "d MMM"); } catch { return ""; } }} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} minTickGap={30} />
                <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} unit="ms" width={48} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" dataKey="response" name="response" stroke={CHART_COLORS.brand} strokeWidth={2} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Bottom bento */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Incident response and timeline: read-only, derived from recorded events. */}
        <Card className="om-premium-card min-w-0 p-4 sm:p-6 lg:col-span-2" data-testid="incident-history">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-blue-700">Reliability operations</p>
              <h3 className="mt-1 font-heading text-lg font-semibold text-slate-900">Incident timeline</h3>
              <p className="mt-1 text-xs leading-5 text-slate-500">Recorded downtime and recovery events for this webapp.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                {incidents.length} recorded
              </span>
              {incidents.some(inc => inc.ongoing) && (
                <span className="rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700" role="status">
                  {incidents.filter(inc => inc.ongoing).length} active
                </span>
              )}
            </div>
          </div>
          {incidentActionError && <p role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{incidentActionError}</p>}
          {incidents.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-10 text-center">
              <p className="font-semibold text-slate-800">No recorded incidents</p>
              <p className="mt-2 text-sm text-slate-500">New downtime events will appear here automatically.</p>
            </div>
          ) : (
            <ol className="relative ml-2 space-y-5 border-l-2 border-slate-200 pl-5 sm:pl-7" aria-label="Incident events">
              {incidents.map((inc) => (
                <li key={inc.id} className="relative min-w-0" data-testid="incident-item">
                  <span aria-hidden="true" className={`absolute -left-[27px] top-3 h-3 w-3 rounded-full border-[3px] border-white ring-2 ring-slate-100 sm:-left-[35px] ${inc.ongoing ? "bg-red-500" : "bg-emerald-500"}`} />
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">
                          {inc.ongoing ? "Active outage" : "Recovered incident"}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">Started {fmtDate(inc.started_at)}</p>
                        {!inc.ongoing && (inc.ended_at || inc.resolved_at) && <p className="mt-1 text-xs text-slate-500">Recovered {fmtDate(inc.ended_at || inc.resolved_at)}</p>}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${inc.ongoing ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                          {inc.ongoing ? "Active · urgent" : "Resolved"}
                        </span>
                        <span className="font-mono text-xs text-slate-500">{fmtDuration(inc.duration_seconds)}</span>
                      </div>
                    </div>
                    {inc.error && (
                      <div className="mt-3 rounded-lg bg-slate-50 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Observed error</p>
                        <p className="mt-1 break-words font-mono text-xs leading-5 text-slate-700">{inc.error}</p>
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                      {inc.acknowledged_at ? (
                        <span className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">Acknowledged</span>
                      ) : (
                        <button type="button" disabled={incidentUpdating === inc.id}
                          onClick={() => updateIncident(inc.id, "acknowledge")}
                          className="min-h-10 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50">
                          Acknowledge
                        </button>
                      )}
                      <button type="button" disabled={incidentUpdating === inc.id}
                        onClick={() => updateIncident(inc.id, inc.assigned_to ? "unassign" : "assign_self")}
                        className="min-h-10 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                        {inc.assigned_to ? "Unassign me" : "Assign to me"}
                      </button>
                    </div>
                    {inc.ongoing && (
                      <details className="mt-3 rounded-lg border border-blue-100 bg-blue-50/50 p-3">
                        <summary className="cursor-pointer text-xs font-semibold text-blue-800">Troubleshooting checklist</summary>
                        <p className="mt-2 text-xs leading-5 text-slate-700">Check your hosting provider, application logs, recent releases, domain DNS and TLS certificates. Verify the issue independently before applying changes.</p>
                        <p className="mt-2 text-xs text-slate-500">Suggested checks only — not a confirmed root cause.</p>
                      </details>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>

        {/* SSL + Domain info */}
        <div className="space-y-6">
          <Card className="border-slate-200 p-6" data-testid="ssl-info">
            <h3 className="mb-2 flex items-center gap-2 font-heading text-base font-semibold text-slate-800"><ShieldCheck size={16} /> SSL Certificate</h3>
            <div className="mb-2"><StatusBadge variant={sslVariant(ssl)}>{ssl.status || "UNKNOWN"}</StatusBadge></div>
            <div className="divide-y divide-slate-100">
              <InfoRow label="Issuer" value={ssl.issuer} />
              <InfoRow label="Expires" value={fmtDay(ssl.expiry)} />
              <InfoRow label="Days left" value={ssl.days_remaining} />
            </div>
            {ssl.error && <p className="mt-2 font-mono text-xs text-slate-400">{ssl.error}</p>}
          </Card>

          <Card className="border-slate-200 p-6" data-testid="domain-info">
            <h3 className="mb-2 flex items-center gap-2 font-heading text-base font-semibold text-slate-800"><Globe2 size={16} /> Domain</h3>
            <div className="mb-2"><StatusBadge variant={domainVariant(domain)}>{domain.status || "UNKNOWN"}</StatusBadge></div>
            <div className="divide-y divide-slate-100">
              <InfoRow label="Domain" value={domain.domain} />
              <InfoRow label="Registrar" value={domain.registrar} />
              <InfoRow label="Expires" value={fmtDay(domain.expiry)} />
              <InfoRow label="Days left" value={domain.days_remaining} />
            </div>
            {domain.error && <p className="mt-2 font-mono text-xs text-slate-400">{domain.error}</p>}
          </Card>
        </div>
      </div>

      {/* Performance breakdown + DNS */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border-slate-200 p-6" data-testid="perf-breakdown">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-semibold text-slate-800"><Zap size={16} /> Performance Breakdown</h3>
          <div className="mb-3"><StatusBadge variant={perfVariant(perf)}>{perf.status || "UNKNOWN"}{perf.page_load_ms != null ? ` · ${(perf.page_load_ms / 1000).toFixed(1)}s` : ""}</StatusBadge></div>
          <div className="divide-y divide-slate-100">
            <InfoRow label="Page load" value={perf.page_load_ms != null ? `${perf.page_load_ms} ms` : "—"} />
            <InfoRow label="DNS lookup" value={perf.dns_ms != null ? `${perf.dns_ms} ms` : "—"} />
            <InfoRow label="Connection" value={perf.connect_ms != null ? `${perf.connect_ms} ms` : "—"} />
            <InfoRow label="TLS handshake" value={perf.tls_ms != null ? `${perf.tls_ms} ms` : "—"} />
            <InfoRow label="TTFB" value={perf.ttfb_ms != null ? `${perf.ttfb_ms} ms` : "—"} />
          </div>
        </Card>

        <Card className="border-slate-200 p-6" data-testid="dns-history">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-semibold text-slate-800"><Network size={16} /> DNS Status</h3>
          <div className="mb-3 flex items-center justify-between">
            <StatusBadge variant={dnsVariant(dns)}>{dns.status || "UNKNOWN"}</StatusBadge>
            <span className="text-xs text-slate-400">{dns.checked_at ? `Checked ${fmtDate(dns.checked_at)}` : ""}</span>
          </div>
          {dns.records && (
            <div className="mb-4 divide-y divide-slate-100">
              {["A", "AAAA", "CNAME", "MX", "NS", "TXT"].map((rt) => (
                <div key={rt} className="flex items-start justify-between gap-4 py-2 text-sm">
                  <span className="font-mono font-medium text-slate-500">{rt}</span>
                  <span className="max-w-[70%] break-all text-right font-mono text-xs text-slate-700">
                    {dns.records[rt]?.length ? dns.records[rt].join(", ") : "—"}
                  </span>
                </div>
              ))}
            </div>
          )}
          {dnsChanges.length > 0 && (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <p className="mb-2 text-[11px] font-mono uppercase tracking-wide text-slate-400">Change history</p>
              <ul className="space-y-3">
                {dnsChanges.map((c) => (
                  <li key={c.id} className="rounded-lg bg-amber-50 p-3 text-xs" data-testid="dns-change-item">
                    <p className="font-mono font-semibold text-amber-700">{c.record_type} Record changed</p>
                    <p className="mt-1 font-mono text-slate-500">OLD: {c.old.join(", ") || "—"}</p>
                    <p className="font-mono text-slate-700">NEW: {c.new.join(", ") || "—"}</p>
                    <p className="mt-1 text-slate-400">{fmtDate(c.detected_at)}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>

      {/* HTTP & Security */}
      <Card className="border-slate-200 p-6" data-testid="http-security">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-800">
            <Lock size={16} /> HTTP &amp; Security
          </h3>
          {http.security_grade && (
            <StatusBadge variant={securityVariant(http)} dot={false} testid="security-grade-badge">
              Grade {http.security_grade} · {http.security_score}/6
            </StatusBadge>
          )}
        </div>

        {http.error ? (
          <p className="font-mono text-xs text-slate-400">{http.error}</p>
        ) : http.checked_at ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Left: status, redirects, http->https */}
            <div>
              <div className="divide-y divide-slate-100">
                <InfoRow label="HTTP Status" value={http.status_code ?? "—"} />
                <div className="flex items-center justify-between py-2.5 text-sm">
                  <span className="text-slate-500">HTTP → HTTPS Redirect</span>
                  <StatusBadge variant={redirectVariant(http)} testid="http-https-badge">
                    {http.http_to_https?.status || "UNKNOWN"}
                  </StatusBadge>
                </div>
                <InfoRow label="Redirects" value={`${http.redirect_count ?? 0} hop${(http.redirect_count ?? 0) === 1 ? "" : "s"}`} />
              </div>

              <p className="mb-2 mt-4 text-[11px] font-mono uppercase tracking-wide text-slate-400">Redirect chain</p>
              {http.redirects && http.redirects.length > 0 ? (
                <ol className="space-y-1.5">
                  {http.redirects.map((r, i) => (
                    <li key={i} className="flex items-center gap-2 font-mono text-xs text-slate-600" data-testid="redirect-hop">
                      <CornerDownRight size={13} className="shrink-0 text-slate-300" />
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-500">{r.status}</span>
                      <span className="truncate">{r.url}</span>
                    </li>
                  ))}
                  <li className="flex items-center gap-2 font-mono text-xs text-slate-800">
                    <ArrowRightLeft size={13} className="shrink-0 text-emerald-500" />
                    <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">200</span>
                    <span className="truncate">{http.final_url}</span>
                  </li>
                </ol>
              ) : (
                <p className="font-mono text-xs text-slate-400">No redirects — direct response.</p>
              )}
            </div>

            {/* Right: security headers checklist */}
            <div>
              <p className="mb-2 text-[11px] font-mono uppercase tracking-wide text-slate-400">Security headers</p>
              <div className="divide-y divide-slate-100">
                {Object.entries(SECURITY_HEADER_LABELS).map(([key, label]) => {
                  const present = http.security_headers?.[key];
                  return (
                    <div key={key} className="flex items-center justify-between py-2.5 text-sm" data-testid={`sec-header-${key}`}>
                      <span className="text-slate-600">{label}</span>
                      {present ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-mono font-medium text-emerald-700">
                          <Check size={12} /> present
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-mono font-medium text-red-600">
                          <XIcon size={12} /> missing
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <p className="py-4 text-center text-sm text-slate-400">Collecting data…</p>
        )}
      </Card>

      <SubdomainDialog websiteId={id} open={showSubs} onClose={() => setShowSubs(false)} onAdded={() => { loadSite(); }} />
      <MonitorIntelligence key={site.id} site={site} />
      <RequestSettings key={`request-${site.id}`} site={site} onSaved={loadSite} />
    </div>
  );
}
